import React, { useState, useEffect, useRef } from 'react';
import Icon from '../shared/Icon';
import { useFlag } from '@unleash/proxy-client-react';
import { apiService } from '../../../services/api';
import { deviceTimeZone } from '../../../shared/datetime/timestamps';
import TimeZonePicker from '../Settings/TimeZonePicker';
import { AVATAR_PALETTE, initialsOf, initialsStyle } from './avatar';
import './account-modals.css';

const ALLOWED_TYPES = ['image/png', 'image/jpeg', 'image/webp'];
const MAX_BYTES = 2 * 1024 * 1024; // 2 MB (mirrors the server limit)
const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

let _teamSeq = 0;
const newTeamId = () => `team-${(_teamSeq += 1)}`;

/**
 * Edit Profile modal (PRD-09). The merged Profile + Personalization flow: one modal, one
 * Save. Profile group (avatar, display name, title, department, About Me) and
 * Personalization group (teams, reviewers) persist together in a single atomic PATCH
 * (apiService.updateProfile → PATCH /api/v1/user-profile), gated server-side on
 * manage_user_context_own (own-record).
 *
 * Time zone: when the organization lets users choose, the user picks a zone or keeps their
 * device's (saved as empty); otherwise the field shows the organization's zone, locked, and is
 * not sent, so a zone saved earlier is kept for when the organization allows it again.
 *
 * Avatar SOURCE (photo | org logo | colour) + colour are saved on Save. Photo BYTES are a
 * separate immediate action (dedicated endpoint validates + re-encodes to WebP + stores in
 * Blob); the photo/org-logo are fetched server-proxied as object URLs because the render
 * endpoints require auth (an <img> can't carry the token).
 *
 * NOTE: teammate/reviewer name+email are user-entered third-party PII — kept client-side
 * only until Save; every write is audited server-side.
 */
function EditProfileModal({ isOpen, user, onClose, onSaved, onShowNotification }) {
  // PRD-09: Teams + Reviewers are placeholders — gated behind this Unleash flag.
  const teamsReviewersEnabled = useFlag('profile-teams-reviewers');
  const [displayName, setDisplayName] = useState('');
  const [jobTitle, setJobTitle] = useState('');
  const [department, setDepartment] = useState('');
  const [about, setAbout] = useState('');
  const [timeZone, setTimeZone] = useState('');
  const [zones, setZones] = useState([]);
  const [zonesStatus, setZonesStatus] = useState('loading');
  const zonesRequest = useRef(0);

  // Avatar draft
  const [avatarType, setAvatarType] = useState('color'); // 'photo' | 'logo' | 'color'
  const [avatarColor, setAvatarColor] = useState('');
  const [photoPreview, setPhotoPreview] = useState(null); // object URL or null
  const [orgLogoPreview, setOrgLogoPreview] = useState(null); // object URL or null

  const [teams, setTeams] = useState([]); // [{ id, name, members:[{name,email}] }]
  const [reviewers, setReviewers] = useState([]); // [{ name, email }]
  const [teamsOpen, setTeamsOpen] = useState(false);
  const [reviewersOpen, setReviewersOpen] = useState(false);

  const [saving, setSaving] = useState(false);
  const [photoBusy, setPhotoBusy] = useState(false);
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);
  const photoUrlRef = useRef(null);
  const logoUrlRef = useRef(null);


  function loadZones() {
    const request = ++zonesRequest.current;
    setZonesStatus('loading');
    apiService.getTimeZones()
      .then((list) => {
        if (request !== zonesRequest.current) return;
        setZones(list);
        setZonesStatus('ready');
      })
      .catch(() => { if (request === zonesRequest.current) setZonesStatus('error'); });
  }

  const setPhotoObjUrl = (objUrl) => {
    if (photoUrlRef.current) URL.revokeObjectURL(photoUrlRef.current);
    photoUrlRef.current = objUrl;
    setPhotoPreview(objUrl);
  };
  const setLogoObjUrl = (objUrl) => {
    if (logoUrlRef.current) URL.revokeObjectURL(logoUrlRef.current);
    logoUrlRef.current = objUrl;
    setOrgLogoPreview(objUrl);
  };

  useEffect(() => {
    if (!isOpen) return undefined;
    const p = user?.profile || {};
    const pers = user?.personalization || {};
    setDisplayName(p.display_name || '');
    setJobTitle(p.job_title || '');
    setDepartment(p.department || '');
    setTimeZone(p.time_zone || '');
    setAbout(pers.about || '');
    setAvatarType(p.avatar_type || (p.photo_url ? 'photo' : 'color'));
    setAvatarColor(p.avatar_color || '');
    setTeams(
      Array.isArray(pers.teams)
        ? pers.teams.map((t) => ({
            id: t.id || newTeamId(),
            name: t.name || '',
            members: Array.isArray(t.members)
              ? t.members.map((m) => ({ name: m.name || '', email: m.email || '' }))
              : [],
          }))
        : []
    );
    setReviewers(
      Array.isArray(pers.reviewers) ? pers.reviewers.map((r) => ({ name: r.name || '', email: r.email || '' })) : []
    );
    setTeamsOpen(Array.isArray(pers.teams) && pers.teams.length > 0);
    setReviewersOpen(Array.isArray(pers.reviewers) && pers.reviewers.length > 0);
    setError('');
    setSaving(false);
    setPhotoBusy(false);

    let cancelled = false;
    if (p.photo_url) {
      apiService.fetchProfilePhotoObjectUrl().then((u) => {
        if (cancelled) {
          if (u) URL.revokeObjectURL(u);
          return;
        }
        setPhotoObjUrl(u);
      });
    } else {
      setPhotoObjUrl(null);
    }
    if (user?.org_time_display_mode === 'users') loadZones();
    if (user?.org_logo_url) {
      apiService.fetchOrgLogoObjectUrl().then((u) => {
        if (cancelled) {
          if (u) URL.revokeObjectURL(u);
          return;
        }
        setLogoObjUrl(u);
      });
    } else {
      setLogoObjUrl(null);
    }
    return () => {
      cancelled = true;
    };
    // Initialise only when the modal OPENS — not on every `user` refresh. An immediate
    // photo upload calls onSaved() which refreshes `user`; re-running this would reset the
    // local avatar selection ('photo') back to the server value and discard unsaved edits.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Revoke outstanding object URLs on unmount.
  useEffect(
    () => () => {
      if (photoUrlRef.current) URL.revokeObjectURL(photoUrlRef.current);
      if (logoUrlRef.current) URL.revokeObjectURL(logoUrlRef.current);
    },
    []
  );

  if (!isOpen) return null;

  const usersChooseZone = user?.org_time_display_mode === 'users';
  const device = deviceTimeZone();
  const orgZone = user?.org_primary_time_zone || 'UTC';

  const handlePhotoPick = () => fileInputRef.current?.click();

  const handlePhotoChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-selecting the same file
    if (!file) return;
    setError('');
    if (!ALLOWED_TYPES.includes(file.type)) {
      setError('Unsupported image type. Use PNG, JPEG, or WebP.');
      return;
    }
    if (file.size > MAX_BYTES) {
      setError('Image exceeds the 2 MB limit.');
      return;
    }
    setPhotoBusy(true);
    try {
      await apiService.uploadProfilePhoto(file);
      const u = await apiService.fetchProfilePhotoObjectUrl();
      setPhotoObjUrl(u);
      setAvatarType('photo'); // select the photo as the source (persisted on Save)
      onShowNotification?.('Photo updated');
      onSaved?.();
    } catch (err) {
      setError(err?.message || 'Failed to upload photo');
    } finally {
      setPhotoBusy(false);
    }
  };

  const handlePhotoRemove = async () => {
    setError('');
    setPhotoBusy(true);
    try {
      await apiService.deleteProfilePhoto();
      setPhotoObjUrl(null);
      if (avatarType === 'photo') setAvatarType('color');
      onSaved?.();
    } catch (err) {
      setError(err?.message || 'Failed to remove photo');
    } finally {
      setPhotoBusy(false);
    }
  };

  const handlePickColor = (hex) => {
    setError('');
    setAvatarColor(hex);
    setAvatarType('color');
  };

  // --- Teams ---
  const addTeam = () => setTeams((prev) => [...prev, { id: newTeamId(), name: '', members: [{ name: '', email: '' }] }]);
  const removeTeam = (id) => setTeams((prev) => prev.filter((t) => t.id !== id));
  const updateTeamName = (id, name) => setTeams((prev) => prev.map((t) => (t.id === id ? { ...t, name } : t)));
  const addMember = (id) =>
    setTeams((prev) => prev.map((t) => (t.id === id ? { ...t, members: [...t.members, { name: '', email: '' }] } : t)));
  const removeMember = (id, mi) =>
    setTeams((prev) => prev.map((t) => (t.id === id ? { ...t, members: t.members.filter((_, i) => i !== mi) } : t)));
  const updateMember = (id, mi, key, val) =>
    setTeams((prev) =>
      prev.map((t) =>
        t.id === id ? { ...t, members: t.members.map((m, i) => (i === mi ? { ...m, [key]: val } : m)) } : t
      )
    );

  // --- Reviewers ---
  const addReviewer = () => setReviewers((prev) => [...prev, { name: '', email: '' }]);
  const removeReviewer = (i) => setReviewers((prev) => prev.filter((_, idx) => idx !== i));
  const updateReviewer = (i, key, val) =>
    setReviewers((prev) => prev.map((r, idx) => (idx === i ? { ...r, [key]: val } : r)));

  const handleSave = async () => {
    setError('');
    // Client-side email validation (server re-validates).
    for (const t of teams) {
      for (const m of t.members) {
        if (m.email && m.email.trim() && !EMAIL_RE.test(m.email.trim())) {
          setError(`Invalid team member email: ${m.email}`);
          return;
        }
      }
    }
    for (const r of reviewers) {
      if (r.email && r.email.trim() && !EMAIL_RE.test(r.email.trim())) {
        setError(`Invalid reviewer email: ${r.email}`);
        return;
      }
    }
    setSaving(true);
    try {
      const payloadTeams = teams
        .map((t) => ({
          id: t.id,
          name: t.name.trim(),
          members: t.members
            .map((m) => ({ name: (m.name || '').trim(), email: (m.email || '').trim() }))
            .filter((m) => m.name || m.email),
        }))
        .filter((t) => t.name || t.members.length);
      const payloadReviewers = reviewers
        .map((r) => ({ name: (r.name || '').trim(), email: (r.email || '').trim() }))
        .filter((r) => r.name || r.email);

      await apiService.updateProfile({
        profile: {
          display_name: displayName,
          job_title: jobTitle,
          department,
          avatar_type: avatarType,
          // Only send a colour when the palette source is chosen; null otherwise.
          avatar_color: avatarType === 'color' ? avatarColor || null : null,
          // Empty is the device's zone. Not sent while the organization sets the zone.
          ...(usersChooseZone ? { time_zone: timeZone } : {}),
        },
        personalization: {
          about,
          teams: payloadTeams,
          reviewers: payloadReviewers,
        },
      });
      onShowNotification?.('Profile updated');
      onSaved?.();
      onClose?.();
    } catch (e) {
      setError(e?.message || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const initials = initialsOf(displayName || user?.name);
  const showPhoto = avatarType === 'photo' && photoPreview;
  const showLogo = avatarType === 'logo' && orgLogoPreview;

  return (
    <div className="account-modal-overlay" onMouseDown={onClose}>
      <div
        className="account-modal"
        onMouseDown={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Edit profile"
      >
        <div className="account-modal-header">
          <h2 className="account-modal-title">Edit Profile</h2>
          <button type="button" className="account-modal-close" onClick={onClose} aria-label="Close">
            <Icon name="close" />
          </button>
        </div>
        <div className="account-modal-body">
          {error && (
            <div className="account-modal-error" role="alert">
              {error}
            </div>
          )}

          {/* Avatar: centered. Set by photo, org logo, or a brand colour. */}
          <div className="account-avatar-block">
            <div
              className="account-avatar-preview"
              aria-hidden="true"
              style={showPhoto || showLogo ? undefined : initialsStyle(displayName || user?.name, avatarColor)}
            >
              {showPhoto ? (
                <img src={photoPreview} alt="" className="account-avatar-img" />
              ) : showLogo ? (
                <img src={orgLogoPreview} alt="" className="account-avatar-img account-avatar-img-contain" />
              ) : initials ? (
                <span className="account-avatar-initials">{initials}</span>
              ) : (
                <Icon name="person" size={40} className="account-avatar-icon" />
              )}
            </div>

            <div className="account-avatar-swatches" role="radiogroup" aria-label="Avatar colour">
              {AVATAR_PALETTE.map((c) => (
                <button
                  type="button"
                  key={c.hex}
                  className={`account-avatar-swatch ${
                    avatarType === 'color' && avatarColor === c.hex ? 'is-selected' : ''
                  }`}
                  style={{ background: c.hex }}
                  onClick={() => handlePickColor(c.hex)}
                  aria-label={c.name}
                  aria-pressed={avatarType === 'color' && avatarColor === c.hex}
                  title={c.name}
                />
              ))}
            </div>

            <div className="account-avatar-or">
              <span>or upload a photo</span>
            </div>
            <div className="account-avatar-actions">
              <button
                type="button"
                className="resui-btn resui-btn--secondary"
                onClick={handlePhotoPick}
                disabled={photoBusy}
              >
                <Icon name="photo_camera" size={16} className="account-btn-icon" />
                {photoBusy ? 'Working…' : 'Upload photo'}
              </button>
              {photoPreview && (
                <button
                  type="button"
                  className="account-btn account-btn-link"
                  onClick={handlePhotoRemove}
                  disabled={photoBusy}
                >
                  Remove photo
                </button>
              )}
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/png,image/jpeg,image/webp"
              onChange={handlePhotoChange}
              style={{ display: 'none' }}
            />
          </div>

          {/* Profile fields */}
          <label className="resui-field">
            <span className="resui-field-label">Display name</span>
            <input
              className="resui-input"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="How your name appears in ResAI"
              autoComplete="name"
            />
          </label>
          <label className="resui-field">
            <span className="resui-field-label">Title</span>
            <input
              className="resui-input"
              value={jobTitle}
              onChange={(e) => setJobTitle(e.target.value)}
              placeholder="e.g. Operations Manager"
              autoComplete="organization-title"
            />
          </label>
          <label className="resui-field">
            <span className="resui-field-label">Department</span>
            <input
              className="resui-input"
              value={department}
              onChange={(e) => setDepartment(e.target.value)}
              autoComplete="organization"
            />
          </label>
          <div className="resui-field">
            <label className="resui-field-label" htmlFor="edit-profile-time-zone">Time zone</label>
            {usersChooseZone ? (
              <TimeZonePicker
                id="edit-profile-time-zone"
                value={timeZone}
                onChange={setTimeZone}
                zones={zones}
                status={zonesStatus}
                onRetry={loadZones}
                noZoneLabel={device ? `Use my device time zone (${device})` : 'Use my device time zone'}
                describedBy="edit-profile-time-zone-help"
              />
            ) : (
              <TimeZonePicker id="edit-profile-time-zone" value={orgZone} onChange={() => {}} disabled describedBy="edit-profile-time-zone-help" />
            )}
            <span id="edit-profile-time-zone-help" className="resui-field-helper">
              {usersChooseZone
                ? `Times across ResAI show in this zone. Report periods such as Last 90 days follow your organization (${orgZone}).`
                : 'Set by your organization. An administrator can allow personal time zones in Settings.'}
            </span>
          </div>
          <label className="resui-field">
            <span className="resui-field-label">About me</span>
            <textarea
              className="resui-textarea"
              rows={3}
              value={about}
              onChange={(e) => setAbout(e.target.value)}
              placeholder="Shared with ResAI to personalise its responses — the datasets you own, the questions you ask, your domain…"
            />
          </label>

          {/* Teams + Reviewers — gated behind the profile-teams-reviewers flag (placeholders). */}
          {teamsReviewersEnabled && (
          <>
          {/* Teams (collapsible) */}
          <div className="account-collapse">
            <button
              type="button"
              className="account-collapse-head"
              aria-expanded={teamsOpen}
              onClick={() => setTeamsOpen((o) => !o)}
            >
              <span className="resui-field-label">Teams</span>
              <Icon name={teamsOpen ? 'expand_less' : 'expand_more'} className="account-collapse-chev" />
            </button>
            {teamsOpen && (
              <div className="account-collapse-body">
                <p className="resui-field-helper">Named groups you share datasets, queries, and automations with</p>
                {teams.map((t) => (
                  <div className="account-team-card" key={t.id}>
                    <div className="account-team-head">
                      <input
                        className="resui-input"
                        value={t.name}
                        onChange={(e) => updateTeamName(t.id, e.target.value)}
                        placeholder="Team name"
                      />
                      <button
                        type="button"
                        className="account-row-remove"
                        aria-label="Remove team"
                        onClick={() => removeTeam(t.id)}
                      >
                        <Icon name="delete_outline" />
                      </button>
                    </div>
                    <div className="account-team-members">
                      {t.members.map((m, mi) => (
                        <div className="account-row" key={`${t.id}-m-${mi}`}>
                          <input
                            className="resui-input"
                            value={m.name}
                            onChange={(e) => updateMember(t.id, mi, 'name', e.target.value)}
                            placeholder="Name"
                          />
                          <input
                            className="resui-input"
                            type="email"
                            value={m.email}
                            onChange={(e) => updateMember(t.id, mi, 'email', e.target.value)}
                            placeholder="name@org.com"
                          />
                          <button
                            type="button"
                            className="account-row-remove"
                            aria-label="Remove teammate"
                            onClick={() => removeMember(t.id, mi)}
                          >
                            <Icon name="close" />
                          </button>
                        </div>
                      ))}
                      <button type="button" className="account-btn account-btn-link" onClick={() => addMember(t.id)}>
                        + Add teammate
                      </button>
                    </div>
                  </div>
                ))}
                <button type="button" className="account-add-row" onClick={addTeam}>
                  <Icon name="add" size={16} className="account-btn-icon" />
                  Add team
                </button>
              </div>
            )}
          </div>

          {/* Reviewers (collapsible) */}
          <div className="account-collapse">
            <button
              type="button"
              className="account-collapse-head"
              aria-expanded={reviewersOpen}
              onClick={() => setReviewersOpen((o) => !o)}
            >
              <span className="resui-field-label">Reviewers</span>
              <Icon name={reviewersOpen ? 'expand_less' : 'expand_more'} className="account-collapse-chev" />
            </button>
            {reviewersOpen && (
              <div className="account-collapse-body">
                <p className="resui-field-helper">People who can review your work</p>
                {reviewers.map((r, i) => (
                  <div className="account-row" key={`rev-${i}`}>
                    <input
                      className="resui-input"
                      value={r.name}
                      onChange={(e) => updateReviewer(i, 'name', e.target.value)}
                      placeholder="Reviewer name"
                    />
                    <input
                      className="resui-input"
                      type="email"
                      value={r.email}
                      onChange={(e) => updateReviewer(i, 'email', e.target.value)}
                      placeholder="name@org.com"
                    />
                    <button
                      type="button"
                      className="account-row-remove"
                      aria-label="Remove reviewer"
                      onClick={() => removeReviewer(i)}
                    >
                      <Icon name="close" />
                    </button>
                  </div>
                ))}
                <button type="button" className="account-btn account-btn-link" onClick={addReviewer}>
                  + Add reviewer
                </button>
              </div>
            )}
          </div>
          </>
          )}
        </div>
        <div className="account-modal-footer">
          <button type="button" className="account-btn account-btn-secondary" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button type="button" className="account-btn account-btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default EditProfileModal;
